<template>
  <div class="template-shell">
    <div v-if="homePage" class="home-container">
      <component
        :is="dynamicComponent"
        :homeImage="homeImage"
        :exhibitIdProps="id"
        @exit="exitHomePage"
      />
    </div>

    <main v-else class="bento-page">
      <header class="bento-header">
        <h1>{{ exhibitName }}</h1>
        <p v-if="userName" class="bento-subtitle">Une exposition par {{ userName }}</p>
      </header>

      <div v-if="loading" class="state-panel">Chargement...</div>
      <div v-else-if="resources.length === 0" class="state-panel">Aucune ressource dans la galerie</div>

      <section v-else class="bento-grid">
        <article
          v-for="(resource, index) in resources"
          :key="resource.id ?? resource.resource_id ?? index"
          :ref="(el) => setTileRef(el, index)"
          class="bento-tile"
          :class="[spanClass(index), { 'is-visible': visibleTiles.has(index) }]"
          :style="{ '--reveal-delay': revealDelay(index) }"
          :data-index="index"
          role="button"
          tabindex="0"
          :aria-label="ariaLabel(resource)"
          @keydown.enter.prevent="openLightbox(index)"
          @keydown.space.prevent="openLightbox(index)"
        >
          <ResourceMedia
            :resource="resource"
            :url="resourceUrls[index]"
            context="thumb"
            fit="cover"
            @open="openLightbox(index)"
          />

          <div class="bento-caption" aria-hidden="true">
            <span class="bento-caption-format">{{ formatLabel(resource) }}</span>
            <h3 v-if="cleanTitle(resource)" class="bento-caption-title">{{ cleanTitle(resource) }}</h3>
          </div>
        </article>
      </section>

      <footer v-if="!loading && resources.length > 0" class="bento-footer">
        <p>{{ resources.length }} ressource{{ resources.length > 1 ? 's' : '' }}</p>
      </footer>
    </main>

    <ResourceLightbox :resources="resources" :urls="resourceUrls" v-model:selectedIndex="selectedIndex" />
  </div>
</template>

<script setup>
import { computed, defineProps, nextTick, onUnmounted, ref, watch } from 'vue';
import { useGalleryTemplate } from '../../composables/useGalleryTemplate';
import { getMediaType } from '../../utils/mediaType';
import { cleanText } from '../../utils/galleryText';
import ResourceMedia from '../../components/gallery/ResourceMedia.vue';
import ResourceLightbox from '../../components/gallery/ResourceLightbox.vue';

/*
 * template_24 — Bento grid.
 * Modern asymmetric grid (CSS Grid, grid-auto-flow: dense), tile spans follow
 * a deterministic cycle adjusted by media format (see IMAGE_CYCLE / tileSpans
 * below). Tiles cascade-reveal on scroll via IntersectionObserver and open
 * the shared ResourceLightbox on any click.
 */

const props = defineProps({
  homePageProps: { type: Boolean, default: true },
  exhibitId: { type: String, default: null },
});

const {
  dynamicComponent,
  exhibitName,
  exitHomePage,
  homeImage,
  homePage,
  id,
  loading,
  resourceUrls,
  resources,
  userName,
} = useGalleryTemplate(props);

/* --------------------------------- lightbox -------------------------------- */
const selectedIndex = ref(null);
const openLightbox = (index) => {
  selectedIndex.value = index;
};

/* ---------------------------- title / format labels ------------------------ */
const cleanTitle = (resource) => cleanText(resource?.title) || cleanText(resource?.name);

const FORMAT_LABELS = {
  image: 'Image',
  video: 'Vidéo',
  audio: 'Audio',
  doc: 'Document',
  other: 'Fichier',
};
const formatLabel = (resource) => FORMAT_LABELS[getMediaType(resource?.name)] || FORMAT_LABELS.other;
const ariaLabel = (resource) => `Ouvrir ${cleanTitle(resource) || formatLabel(resource)}`;

/* --------------------------------- tile spans ------------------------------- */
// Deterministic cycle (no randomness -> no unpredictable holes; grid-auto-flow:
// dense fills any remaining gaps). Video/audio/doc get a fixed span from their
// format; images (and unrecognised formats) advance through the cycle below.
const IMAGE_CYCLE = [
  { cols: 2, rows: 2 },
  { cols: 1, rows: 1 },
  { cols: 1, rows: 1 },
  { cols: 2, rows: 1 },
  { cols: 1, rows: 1 },
  { cols: 1, rows: 2 },
];

const tileSpans = computed(() => {
  let cycleIndex = 0;
  return resources.value.map((resource) => {
    const type = getMediaType(resource?.name);
    if (type === 'video') return { cols: 2, rows: 2 };
    if (type === 'doc') return { cols: 1, rows: 2 };
    if (type === 'audio') return { cols: 1, rows: 1 };
    const span = IMAGE_CYCLE[cycleIndex % IMAGE_CYCLE.length];
    cycleIndex += 1;
    return span;
  });
});

const spanClass = (index) => {
  const span = tileSpans.value[index] || { cols: 1, rows: 1 };
  return `span-${span.cols}x${span.rows}`;
};

/* ------------------------------ cascade reveal ------------------------------ */
const visibleTiles = ref(new Set());
const revealDelay = (index) => `${Math.min(index * 60, 480)}ms`;

let tileEls = [];
const setTileRef = (el, index) => {
  if (el) tileEls[index] = el;
};

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  !!window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

let observer = null;
const teardownObserver = () => {
  if (observer) {
    observer.disconnect();
    observer = null;
  }
};

const setupObserver = () => {
  teardownObserver();
  if (!resources.value.length) return;

  if (prefersReducedMotion() || typeof IntersectionObserver === 'undefined') {
    resources.value.forEach((_, index) => visibleTiles.value.add(index));
    return;
  }

  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const index = Number(entry.target.dataset.index);
        visibleTiles.value.add(index);
        observer.unobserve(entry.target);
      });
    },
    { threshold: 0.15, rootMargin: '0px 0px -10% 0px' }
  );
  tileEls.forEach((el) => el && observer.observe(el));
};

// The gallery grid only exists in the DOM once the home page has been left
// (v-else branch) *and* resources have loaded, so both must be watched:
// resources can resolve before or after the user exits the home page.
watch(
  [resources, homePage],
  async () => {
    tileEls = [];
    visibleTiles.value.clear();
    if (homePage.value || !resources.value.length) {
      teardownObserver();
      return;
    }
    await nextTick();
    setupObserver();
  },
  { immediate: true }
);

onUnmounted(() => {
  teardownObserver();
});
</script>

<style scoped>
.template-shell {
  min-height: 100vh;
}

.bento-page {
  --bento-bg: #f7f5f1;
  --bento-ink: #1c1b19;
  --bento-muted: #837c73;
  --bento-border: rgba(28, 27, 25, 0.08);
  min-height: 100vh;
  background: var(--bento-bg);
  color: var(--bento-ink);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif;
  padding: 56px 64px 72px;
  box-sizing: border-box;
}

.bento-header {
  max-width: 1400px;
  margin: 0 auto 40px;
  text-align: center;
}

.bento-header h1 {
  margin: 0;
  font-size: 44px;
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1.1;
}

.bento-subtitle {
  margin: 10px 0 0;
  color: var(--bento-muted);
  font-size: 16px;
}

.state-panel {
  max-width: 1400px;
  margin: 0 auto;
  padding: 100px 20px;
  text-align: center;
  color: var(--bento-muted);
  font-size: 17px;
}

.bento-grid {
  max-width: 1400px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-auto-rows: 140px;
  grid-auto-flow: dense;
  gap: 22px;
}

.bento-tile {
  position: relative;
  overflow: hidden;
  grid-column: span 1;
  grid-row: span 1;
  border-radius: 20px;
  border: 1px solid var(--bento-border);
  background: #fff;
  cursor: pointer;
  opacity: 0;
  transform: translateY(26px);
  transition: opacity 640ms ease, transform 640ms ease, box-shadow 220ms ease;
  /* Cascade delay only on the reveal properties (opacity/transform); the hover
     box-shadow elevation must react immediately, whatever the tile's index. */
  transition-delay: var(--reveal-delay, 0ms), var(--reveal-delay, 0ms), 0ms;
}

.bento-tile.is-visible {
  opacity: 1;
  transform: translateY(0);
}

.bento-tile:hover,
.bento-tile:focus-visible {
  box-shadow: 0 18px 40px rgba(28, 27, 25, 0.14);
}

.bento-tile:focus-visible {
  outline: 2px solid var(--bento-ink);
  outline-offset: 3px;
}

.bento-tile.span-2x2 {
  grid-column: span 2;
  grid-row: span 2;
}

.bento-tile.span-2x1 {
  grid-column: span 2;
  grid-row: span 1;
}

.bento-tile.span-1x2 {
  grid-column: span 1;
  grid-row: span 2;
}

.bento-tile :deep(.resource-media) {
  width: 100%;
  height: 100%;
}

.bento-tile :deep(.rm-media) {
  transition: transform 680ms cubic-bezier(0.16, 1, 0.3, 1);
}

.bento-tile:hover :deep(.rm-media),
.bento-tile:focus-visible :deep(.rm-media) {
  transform: scale(1.07);
}

.bento-caption {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 34px 18px 14px;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.68), transparent);
  color: #fff;
  pointer-events: none;
  opacity: 0;
  transform: translateY(10px);
  transition: opacity 220ms ease, transform 220ms ease;
}

.bento-tile:hover .bento-caption,
.bento-tile:focus-visible .bento-caption {
  opacity: 1;
  transform: translateY(0);
}

.bento-caption-format {
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.78);
}

.bento-caption-title {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.bento-footer {
  max-width: 1400px;
  margin: 48px auto 0;
  padding-top: 22px;
  border-top: 1px solid var(--bento-border);
  text-align: center;
}

.bento-footer p {
  margin: 0;
  color: var(--bento-muted);
  font-size: 13px;
  letter-spacing: 0.02em;
}

@media (prefers-reduced-motion: reduce) {
  .bento-tile {
    transition: none;
    opacity: 1;
    transform: none;
  }

  .bento-tile :deep(.rm-media) {
    transition: none;
  }

  .bento-tile:hover :deep(.rm-media),
  .bento-tile:focus-visible :deep(.rm-media) {
    transform: none;
  }

  .bento-caption {
    transition: none;
  }
}

@media (max-width: 980px) {
  .bento-page {
    padding: 44px 32px 56px;
  }

  .bento-header h1 {
    font-size: 34px;
  }

  .bento-grid {
    grid-template-columns: repeat(2, 1fr);
    grid-auto-rows: 150px;
    gap: 18px;
  }
}

@media (max-width: 600px) {
  .bento-page {
    padding: 32px 18px 44px;
  }

  .bento-header {
    margin-bottom: 28px;
  }

  .bento-header h1 {
    font-size: 28px;
  }

  .bento-grid {
    grid-auto-rows: 130px;
    gap: 14px;
  }

  .bento-tile {
    border-radius: 16px;
  }
}

@media (max-width: 420px) {
  .bento-grid {
    grid-template-columns: 1fr;
    grid-auto-rows: 140px;
  }

  .bento-tile.span-2x2,
  .bento-tile.span-2x1 {
    grid-column: span 1;
  }
}
</style>
